// Wspólne elementy nowego wyglądu PDF (v2 = "color", v2 uproszczony = "simple") dla raportu, FUL i CL.
// Źródło: design_handoff_pdf_documents (MonitoringReportV3) i Design.pdf. Komponenty powstają przez
// makeKit(variant) przy każdym renderze dokumentu (licznik sekcji 01, 02, … jest per dokument).
import { Image, Text, View } from "@react-pdf/renderer";
import type { Style } from "@react-pdf/types";
import { ReactNode } from "react";
import "../pdfFonts";

const logoUrl = require("../../../../assets/logo-scientia.png");

export type DesignVariant = "color" | "simple";
export type Tone = "red" | "amber" | "green" | "grey";
export type StatusState = { label: string; open: boolean; tone: Tone };

/* ----- tokeny (tokens/document.css + Design.pdf) ----- */
export const C = {
  text: "#1A1A18",
  muted: "#5C5C59",
  row: "#D6D6D3",
  sep: "#BDBDBA",
  panel: "#F5F5F3",
  accent: "#EB7C31",
  accentText: "#B4561A",
  accentBg: "#FDF1E8",
  accentLine: "#F2C4A3",
  redText: "#B42318",
  redBg: "#FBE7E6",
  greenText: "#1E7A4C",
  greenBg: "#E4F3EA",
  greyPill: "#EDEDEA",
};

export const PAGE_MARGIN = 39.6; // 0.55in

export const pageStyle: Style = {
  fontFamily: "Roboto",
  fontSize: 9,
  color: C.text,
  lineHeight: 1.45,
  paddingTop: 40,
  paddingBottom: 56,
  paddingHorizontal: PAGE_MARGIN,
};

/* ----- pomocnicze ----- */
export const fmt = (d?: Date | string | null) => {
  if (!d) return "—";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "—";
  return `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(2, "0")}.${date.getFullYear()}`;
};
export const weekday = (d: Date | string, short: boolean) =>
  new Date(d).toLocaleDateString("pl-PL", { weekday: short ? "short" : "long" }).replace(/\.$/, "");
export const capitalizeWords = (s: string) => s.replace(/(^|\s|-)\S/g, (m) => m.toUpperCase());

export type SiteStatusData = {
  recruitmentPct: number;
  randomized: number;
  declared: number;
  protocolDeviation: number;
  sae: number;
  counts: [string, number | undefined | null][];
  columns?: number; // liczników w wierszu (domyślnie 4)
};
export type MonitoredRow = { participantNumber: string; visitNumber?: string; sdv: boolean; rbv: boolean; comment?: string };

export const makeKit = (variant: DesignVariant) => {
  const color = variant === "color";
  let sectionNo = 0;

  const Muted = ({ children, style }: { children: ReactNode; style?: Style | Style[] }) => (
    <Text style={[{ color: C.muted }, ...(Array.isArray(style) ? style : style ? [style] : [])]}>{children}</Text>
  );

  const Pill = ({ label, tone, solid }: { label: string; tone: Tone; solid?: boolean }) => {
    const palette = {
      red: { bg: solid ? C.redText : C.redBg, fg: solid ? "#FFFFFF" : C.redText },
      amber: { bg: C.accentBg, fg: C.accentText },
      green: { bg: C.greenBg, fg: C.greenText },
      grey: { bg: C.greyPill, fg: C.muted },
    }[tone];
    return (
      <View style={{ alignSelf: "flex-start", backgroundColor: palette.bg, borderRadius: 6, paddingVertical: 1, paddingHorizontal: 5 }}>
        <Text style={{ fontSize: 7, fontWeight: 600, color: palette.fg }}>{label}</Text>
      </View>
    );
  };

  // Status: v2 = kolorowy znacznik; v2 uproszczony = kropka (otwarte) / pierścień (zamknięte) + tekst
  const Status = ({ state }: { state: StatusState }) =>
    color ? (
      <Pill label={state.label} tone={state.tone} />
    ) : (
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        {state.open ? (
          <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: C.text, marginRight: 4 }} />
        ) : (
          <View style={{ width: 5, height: 5, borderRadius: 2.5, borderWidth: 0.75, borderColor: C.text, marginRight: 4 }} />
        )}
        <Text>{state.label}</Text>
      </View>
    );

  const StatusLegend = () => (
    <View style={{ flexDirection: "row", alignItems: "center" }}>
      <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: C.text, marginRight: 3 }} />
      <Muted style={{ fontSize: 8, marginRight: 8 }}>otwarte</Muted>
      <View style={{ width: 5, height: 5, borderRadius: 2.5, borderWidth: 0.75, borderColor: C.text, marginRight: 3 }} />
      <Muted style={{ fontSize: 8 }}>zamknięte</Muted>
    </View>
  );

  const YesNo = ({ value }: { value: boolean }) =>
    color ? <Pill label={value ? "TAK" : "NIE"} tone={value ? "green" : "grey"} /> : <Text>{value ? "Tak" : "Nie"}</Text>;

  const SectionHeader = ({ title, right }: { title: string; right?: ReactNode }) => {
    sectionNo += 1;
    const no = String(sectionNo).padStart(2, "0");
    return color ? (
      <View minPresenceAhead={60} style={{ flexDirection: "row", alignItems: "flex-end", marginTop: 20, marginBottom: 8, paddingBottom: 4, borderBottomWidth: 0.75, borderBottomColor: C.accentLine }}>
        <Text style={{ fontSize: 8, fontWeight: 600, color: C.accentText, marginRight: 8, marginBottom: 1 }}>{no}</Text>
        <Text style={{ fontSize: 12, fontWeight: 600 }}>{title}</Text>
        {right && <View style={{ marginLeft: "auto" }}>{right}</View>}
      </View>
    ) : (
      <View minPresenceAhead={60} style={{ flexDirection: "row", alignItems: "flex-end", marginTop: 22, marginBottom: 8, paddingBottom: 4, borderBottomWidth: 0.5, borderBottomColor: C.text }}>
        <Text style={{ fontSize: 8, color: C.muted, marginRight: 10 }}>{no}</Text>
        <Text style={{ fontSize: 9, fontWeight: 600, letterSpacing: 0.9, textTransform: "uppercase" }}>{title}</Text>
        {right && <View style={{ marginLeft: "auto" }}>{right}</View>}
      </View>
    );
  };

  // Tabela: nagłówek + wiersze, szerokości w %. Wiersze nie dzielą się między strony.
  const Table = ({ columns, rows }: { columns: { label: string; width: string; align?: "right" | "center" }[]; rows: ReactNode[][] }) => {
    const alignItems = (a?: "right" | "center") => (a === "right" ? "flex-end" : a === "center" ? "center" : "flex-start");
    return (
      <View style={{ fontSize: 8.5 }}>
        <View
          style={[
            { flexDirection: "row", fontSize: 7.5, color: C.muted },
            color
              ? { backgroundColor: C.panel, textTransform: "uppercase", letterSpacing: 0.4 }
              : { borderBottomWidth: 0.75, borderBottomColor: C.text },
          ]}
        >
          {columns.map((c, i) => (
            <View key={i} style={{ width: c.width, paddingVertical: color ? 6 : 4, paddingHorizontal: 6, alignItems: alignItems(c.align) }}>
              <Text>{c.label}</Text>
            </View>
          ))}
        </View>
        {rows.map((cells, r) => (
          <View key={r} wrap={false} style={{ flexDirection: "row", borderBottomWidth: 0.5, borderBottomColor: C.row }}>
            {cells.map((cell, i) => (
              <View key={i} style={{ width: columns[i].width, paddingVertical: 5, paddingHorizontal: 6, alignItems: alignItems(columns[i].align) }}>
                {typeof cell === "string" ? <Text>{cell}</Text> : cell}
              </View>
            ))}
          </View>
        ))}
      </View>
    );
  };

  // Tytuł: v2 = znaczek + notka + nagłówek; v2 uproszczony = nad-tytuł + nagłówek
  const Title = ({ badge, note, overline, heading }: { badge: string; note?: string; overline: string; heading: string }) =>
    color ? (
      <View style={{ marginBottom: 14 }}>
        <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 8 }}>
          <View style={{ backgroundColor: C.accentBg, borderRadius: 3, paddingVertical: 2, paddingHorizontal: 6, marginRight: 8 }}>
            <Text style={{ fontSize: 7.5, fontWeight: 600, letterSpacing: 0.6, color: C.accentText, textTransform: "uppercase" }}>{badge}</Text>
          </View>
          {!!note && <Muted style={{ fontSize: 8 }}>{note}</Muted>}
        </View>
        <Text style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.25 }}>{heading}</Text>
      </View>
    ) : (
      <View style={{ marginBottom: 14 }}>
        <Muted style={{ fontSize: 8, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>{overline}</Muted>
        <Text style={{ fontSize: 20, fontWeight: 500, lineHeight: 1.2 }}>{heading}</Text>
      </View>
    );

  // Metadane: v2 = szary panel w 3 kolumnach; v2 uproszczony = 2 kolumny z liniami
  const MetaBlock = ({ items }: { items: [string, string][] }) =>
    color ? (
      <View style={{ backgroundColor: C.panel, borderRadius: 4, paddingVertical: 10, paddingHorizontal: 14, flexDirection: "row", flexWrap: "wrap" }}>
        {items.map(([k, v]) => (
          <View key={k} style={{ width: "33.33%", paddingVertical: 4, paddingRight: 8 }}>
            <Muted style={{ fontSize: 7, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 2 }}>{k}</Muted>
            <Text style={{ fontWeight: 600 }}>{v}</Text>
          </View>
        ))}
      </View>
    ) : (
      <View style={{ flexDirection: "row", flexWrap: "wrap", justifyContent: "space-between", borderTopWidth: 1, borderTopColor: C.text }}>
        {items.map(([k, v]) => (
          <View key={k} style={{ width: "47%", flexDirection: "row", paddingVertical: 4, borderBottomWidth: 0.5, borderBottomColor: C.row }}>
            <Muted style={{ width: 86, marginRight: 8 }}>{k}</Muted>
            <Text style={{ flex: 1, fontWeight: 500 }}>{v}</Text>
          </View>
        ))}
      </View>
    );

  // Wiersze „etykieta: wartość” (uczestnicy wizyty, zespół, kontakt)
  const LabeledRows = ({ rows }: { rows: [string, string | undefined | null][] }) => (
    <View>
      {rows.map(([k, v]) => (
        <View key={k} style={{ flexDirection: "row", marginBottom: 4 }}>
          <Muted style={[{ width: 70, marginRight: 8 }, color ? { fontSize: 7.5, textTransform: "uppercase", letterSpacing: 0.5, paddingTop: 1 } : {}]}>{k}</Muted>
          <Text style={{ flex: 1 }}>{v || "—"}</Text>
        </View>
      ))}
    </View>
  );

  // Status ośrodka: 3 wskaźniki + liczniki. W v2 kafelki PD i SAE kolorowe tylko przy wartości > 0.
  const SiteStatusBlock = ({ data }: { data: SiteStatusData }) => (
    <View wrap={false}>
      {color ? (
        <View style={{ flexDirection: "row", marginBottom: 8 }}>
          <View style={{ width: "48%", borderWidth: 0.75, borderColor: C.row, borderRadius: 4, padding: 10, marginRight: "2%" }}>
            <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "flex-end" }}>
              <Muted style={{ fontSize: 7.5, textTransform: "uppercase", letterSpacing: 0.5 }}>Status rekrutacji</Muted>
              <Text style={{ fontSize: 16, fontWeight: 600 }}>{`${data.recruitmentPct}%`}</Text>
            </View>
            <View style={{ height: 3, backgroundColor: C.greyPill, borderRadius: 2, marginTop: 5, marginBottom: 5 }}>
              <View style={{ height: 3, width: `${Math.min(100, Math.max(0, data.recruitmentPct))}%`, backgroundColor: C.accent, borderRadius: 2 }} />
            </View>
            <Muted style={{ fontSize: 7.5 }}>{`${data.randomized} zrandomizowanych z ${data.declared} zadeklarowanych`}</Muted>
          </View>
          {(
            [
              ["Odchylenia od protokołu", data.protocolDeviation, C.accentBg, C.accentText, "2%"],
              ["SAE", data.sae, C.redBg, C.redText, 0],
            ] as const
          ).map(([label, value, bg, fg, gap]) => {
            const alert = value > 0;
            return (
              <View
                key={label}
                style={[
                  { width: "24%", borderRadius: 4, padding: 10, marginRight: gap },
                  alert ? { backgroundColor: bg } : { backgroundColor: "#FFFFFF", borderWidth: 0.75, borderColor: C.row },
                ]}
              >
                <Text style={{ fontSize: 7.5, textTransform: "uppercase", letterSpacing: 0.5, color: alert ? fg : C.muted }}>{label}</Text>
                <Text style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}>{String(value)}</Text>
              </View>
            );
          })}
        </View>
      ) : (
        <View style={{ flexDirection: "row", marginBottom: 10 }}>
          {(
            [
              [`${data.recruitmentPct}%`, `Status rekrutacji (${data.randomized} z ${data.declared})`],
              [String(data.protocolDeviation), "Odchylenia od protokołu"],
              [String(data.sae), "SAE"],
            ] as const
          ).map(([v, l]) => (
            <View key={l} style={{ width: "33.33%", paddingVertical: 2, paddingHorizontal: 14, borderLeftWidth: 0.5, borderLeftColor: C.sep }}>
              <Text style={{ fontSize: 22, fontWeight: 300, lineHeight: 1.1 }}>{v}</Text>
              <Muted style={{ fontSize: 8 }}>{l}</Muted>
            </View>
          ))}
        </View>
      )}
      <View
        style={[
          { flexDirection: "row", flexWrap: "wrap" },
          color ? { borderWidth: 0.75, borderColor: C.row, borderRadius: 4 } : { borderTopWidth: 0.5, borderTopColor: C.sep },
        ]}
      >
        {data.counts.map(([l, v], i) => {
          const cols = data.columns ?? 4;
          const lastRowStart = data.counts.length - (data.counts.length % cols || cols);
          return (
            <View
              key={l}
              style={[
                { width: `${100 / cols}%`, paddingVertical: 5, paddingRight: 10 },
                color
                  ? { paddingLeft: 10, borderRightWidth: i % cols === cols - 1 ? 0 : 0.75, borderRightColor: C.row, borderBottomWidth: i < lastRowStart ? 0.75 : 0, borderBottomColor: C.row }
                  : { borderBottomWidth: 0.5, borderBottomColor: C.row },
              ]}
            >
              <Muted style={{ fontSize: 7.5 }}>{l}</Muted>
              <Text style={{ fontSize: 11, fontWeight: color ? 600 : 500 }}>{String(v ?? 0)}</Text>
            </View>
          );
        })}
      </View>
    </View>
  );

  const MonitoredDataTable = ({ rows }: { rows: MonitoredRow[] }) => (
    <Table
      columns={[
        { label: "Uczestnik", width: "15%" },
        { label: "Zweryf. wizyty", width: "15%" },
        { label: "100% SDV", width: "11%" },
        { label: "RBV", width: "9%" },
        { label: "Komentarz", width: "50%" },
      ]}
      rows={rows.map((p) => [
        <Text style={{ fontWeight: color ? 600 : 500 }}>{p.participantNumber}</Text>,
        p.visitNumber || "—",
        <YesNo value={p.sdv} />,
        <YesNo value={p.rbv} />,
        p.comment || "—",
      ])}
    />
  );

  // Akapit treści listu / komentarza
  const Paragraph = ({ children, style }: { children: ReactNode; style?: Style }) => (
    <Text style={[{ fontSize: 9.5, lineHeight: 1.55, marginBottom: 6 }, style ?? {}]}>{children}</Text>
  );

  const PageHeader = () => (
    <View fixed style={{ marginBottom: 16 }}>
      <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", paddingBottom: 8 }}>
        <Text style={{ fontSize: 7.5, letterSpacing: 0.75, color: C.muted }}>PROVIDING THE BEST IN CLINICAL RESEARCH</Text>
        <Image src={logoUrl} style={{ width: "4cm", height: "1.2cm", objectFit: "contain", objectPosition: "right center" }} />
      </View>
      <View style={{ borderBottomWidth: 0.75, borderBottomColor: C.accent }} />
    </View>
  );

  // Stopka: tekst firmy po lewej, identyfikacja dokumentu + numer strony po prawej
  const PageFooter = ({ left, right }: { left?: string; right: string }) => (
    <View
      fixed
      style={{ position: "absolute", left: PAGE_MARGIN, right: PAGE_MARGIN, bottom: 24, flexDirection: "row", justifyContent: "space-between", paddingTop: 8, borderTopWidth: 0.5, borderTopColor: C.sep, fontSize: 7.5, color: C.muted }}
    >
      <Text style={{ maxWidth: "62%" }}>{left ?? ""}</Text>
      <Text render={({ pageNumber, totalPages }) => [right, `${pageNumber}/${totalPages}`].filter(Boolean).join(" · ")} />
    </View>
  );

  // Lista punktowana (zespół, wymagany dostęp); wiodące „–”/„-”/„•” z danych są pomijane
  const BulletList = ({ items }: { items: string[] }) => (
    <View style={{ marginTop: 2 }}>
      {items.map((item, i) => (
        <View key={i} style={{ flexDirection: "row", marginBottom: 2 }} wrap={false}>
          <Text style={{ width: 12, color: color ? C.accentText : C.muted }}>–</Text>
          <Text style={{ flex: 1 }}>{item.replace(/^\s*[–\-•]\s*/, "")}</Text>
        </View>
      ))}
    </View>
  );
  const linesOf = (text?: string | null) => (text ?? "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

  // Zakończenie listu
  const LetterSignature = ({ author }: { author: string }) => (
    <View wrap={false} style={{ marginTop: 22 }}>
      <Text style={{ fontSize: 9.5 }}>Z poważaniem,</Text>
      <Text style={{ fontSize: 9.5, fontWeight: color ? 600 : 500, marginTop: 2 }}>{author}</Text>
    </View>
  );

  // Podpisy (raport) albo „Z poważaniem” (listy)
  const SignatureFields = ({ fields }: { fields: [string, string][] }) => (
    <View wrap={false} style={{ flexDirection: "row", justifyContent: "space-between", marginTop: 40 }}>
      {fields.map(([l, v]) => (
        <View key={l} style={{ width: "47%" }}>
          <View style={{ height: 26, borderBottomWidth: 0.5, borderBottomColor: C.text }} />
          <Muted style={[{ fontSize: 7.5, marginTop: 3 }, color ? { letterSpacing: 0.5 } : {}]}>{color ? l.toUpperCase() : l}</Muted>
          <Text style={{ fontWeight: color ? 600 : 500 }}>{v}</Text>
        </View>
      ))}
    </View>
  );

  return {
    color,
    Muted,
    Pill,
    Status,
    StatusLegend,
    YesNo,
    SectionHeader,
    Table,
    Title,
    MetaBlock,
    LabeledRows,
    SiteStatusBlock,
    MonitoredDataTable,
    Paragraph,
    PageHeader,
    PageFooter,
    SignatureFields,
    BulletList,
    linesOf,
    LetterSignature,
  };
};
